import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  Stack,
  Chip,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
  Divider,
  Avatar,
  Paper,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Close,
  AccessTime,
  CalendarToday,
  CheckCircle,
  Warning,
  Info,
  Refresh,
  Schedule,
  EventAvailable,
  EventBusy,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAvailabilities,
  addAvailability,
  updateAvailability,
  deleteAvailability,
} from '../../features/availability/availabilitySlice';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'active' ? alpha(theme.palette.success.main, 0.1) :
                  status === 'warning' ? alpha(theme.palette.warning.main, 0.1) :
                  alpha(theme.palette.error.main, 0.1),
  color: status === 'active' ? theme.palette.success.main :
         status === 'warning' ? theme.palette.warning.main :
         theme.palette.error.main,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const daysOfWeek = [
  { value: 'Mon', label: 'Monday', color: '#FF6B6B' },
  { value: 'Tue', label: 'Tuesday', color: '#4ECDC4' },
  { value: 'Wed', label: 'Wednesday', color: '#45B7D1' },
  { value: 'Thu', label: 'Thursday', color: '#96CEB4' },
  { value: 'Fri', label: 'Friday', color: '#FFEEAD' },
  { value: 'Sat', label: 'Saturday', color: '#D4A5A5' },
  { value: 'Sun', label: 'Sunday', color: '#9B59B6' },
];

export default function AvailabilityManager() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { availabilities, loading, error } = useSelector((state) => state.availability);

  const [formData, setFormData] = useState({
    day: '',
    start_time: '',
    end_time: '',
    slot_duration: 30,
  });
  const [editData, setEditData] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    dispatch(fetchAvailabilities());
  }, [dispatch]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openEditDialog = (availability) => {
    setEditData(availability);
    setFormData(availability);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editData) {
        await dispatch(updateAvailability({ id: editData.id, data: formData })).unwrap();
        setSnackbar({
          open: true,
          message: 'Availability updated successfully!',
          severity: 'success'
        });
      } else {
        await dispatch(addAvailability(formData)).unwrap();
        setSnackbar({
          open: true,
          message: 'Availability added successfully!',
          severity: 'success'
        });
      }
      setDialogOpen(false);
      setFormData({ day: '', start_time: '', end_time: '', slot_duration: 30 });
      setEditData(null);
      // Refresh availability list after successful operation
      await dispatch(fetchAvailabilities());
    } catch (err) {
      const errorMessage = err?.detail || err?.error || err?.message || err || 'An error occurred';
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this availability?')) {
      try {
        await dispatch(deleteAvailability(id)).unwrap();
        setSnackbar({
          open: true,
          message: 'Availability deleted successfully!',
          severity: 'success'
        });
        // Refresh availability list after successful deletion
        dispatch(fetchAvailabilities());
      } catch (err) {
        const errorMessage = err?.detail || err?.error || err?.message || err || 'An error occurred';
        setSnackbar({
          open: true,
          message: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
          severity: 'error'
        });
      }
    }
  };

  const getDayAvailability = (day) => {
    return availabilities.filter(avail => avail.day === day);
  };

  const getAvailabilityStatus = (day) => {
    const dayAvailabilities = getDayAvailability(day);
    if (dayAvailabilities.length === 0) return 'inactive';
    if (dayAvailabilities.length >= 4) return 'active';
    return 'warning';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Manage Your Availability
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Set your working hours and let patients know when you're available
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => dispatch(fetchAvailabilities())}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            Add Availability
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Slots
                  </Typography>
                  <AccessTime color="primary" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {availabilities.length}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Available Days
                  </Typography>
                  <CalendarToday color="success" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {new Set(availabilities.map(avail => avail.day)).size}
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Average Duration
                  </Typography>
                  <Schedule color="warning" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {availabilities.length > 0
                    ? Math.round(availabilities.reduce((acc, curr) => acc + curr.slot_duration, 0) / availabilities.length)
                    : 0} mins
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Coverage
                  </Typography>
                  <EventAvailable color="info" />
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {Math.round((new Set(availabilities.map(avail => avail.day)).size / 7) * 100)}%
                </Typography>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Days Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {daysOfWeek.map((day) => (
          <Grid item xs={12} sm={6} md={4} key={day.value}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StyledCard
                sx={{
                  cursor: 'pointer',
                  border: selectedDay === day.value ? `2px solid ${day.color}` : undefined,
                }}
                onClick={() => setSelectedDay(day.value)}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ color: day.color }}>
                        {day.label}
                      </Typography>
                      <StatusChip
                        icon={getAvailabilityStatus(day.value) === 'active' ? <CheckCircle /> :
                              getAvailabilityStatus(day.value) === 'warning' ? <Warning /> :
                              <EventBusy />}
                        label={getAvailabilityStatus(day.value) === 'active' ? 'Available' :
                               getAvailabilityStatus(day.value) === 'warning' ? 'Limited' :
                               'Unavailable'}
                        status={getAvailabilityStatus(day.value)}
                        size="small"
                      />
                    </Box>
                    <Divider />
                    {getDayAvailability(day.value).length > 0 ? (
                      <Stack spacing={1}>
                        {getDayAvailability(day.value).map((avail) => (
                          <Box
                            key={avail.id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 1,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            }}
                          >
                            <Typography variant="body2">
                              {avail.start_time} - {avail.end_time}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {avail.slot_duration} mins
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" align="center">
                        No slots available
                      </Typography>
                    )}
                    <Button
                      startIcon={<Add />}
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({ ...formData, day: day.value });
                        setDialogOpen(true);
                      }}
                    >
                      Add Slot
                    </Button>
                  </Stack>
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Detailed View */}
      {selectedDay && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <StyledCard>
            <CardContent>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {daysOfWeek.find(d => d.value === selectedDay)?.label} Schedule
                </Typography>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  onClick={() => {
                    setFormData({ ...formData, day: selectedDay });
                    setDialogOpen(true);
                  }}
                >
                  Add Slot
                </Button>
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getDayAvailability(selectedDay).map((availability) => (
                    <TableRow key={availability.id}>
                      <TableCell>{availability.start_time}</TableCell>
                      <TableCell>{availability.end_time}</TableCell>
                      <TableCell>{availability.slot_duration} mins</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(availability)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(availability.id)}
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </StyledCard>
        </motion.div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {editData ? 'Edit Availability' : 'Add Availability'}
            <IconButton onClick={() => setDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Select
                name="day"
                value={formData.day}
                onChange={handleChange}
                fullWidth
                required
                label="Day"
              >
                {daysOfWeek.map((day) => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                type="time"
                name="start_time"
                label="Start Time"
                value={formData.start_time}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="time"
                name="end_time"
                label="End Time"
                value={formData.end_time}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="number"
                name="slot_duration"
                label="Slot Duration (minutes)"
                value={formData.slot_duration}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ min: 15, max: 120, step: 15 }}
              />
            </Stack>
            <DialogActions sx={{ mt: 3 }}>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : editData ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
